import dayjs from "dayjs";
import React, { FC, useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { IdNameList } from "../../../../../../../../api/services/common/models";
import {
  useGetCurrencies,
  useGetSkills,
  useGetTargetIndustries,
  useGetTechnicalskills,
} from "../../../../../../../../api/services/program/programApis";
import {
  FormController,
  FormControllerCheckbox,
  MuiBox,
  MuiCheckbox,
  MuiDateFieldFormController,
  MuiGrid,
  MuiMultiSelect,
  MuiSelect,
  MuiTextField,
  MuiTypography,
} from "../../../../../../../../components";
import { MuiInfoToolTip } from "../../../../../../../../components/tooltip/infoToolTip/MuiInfoToolTip";
import {
  MAX_LENGTH_100
} from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  getValueOrBlank,
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useMultiSelectARIALabels } from "../../../../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import {
  toggleLoader,
  toggleToast,
} from "../../../../../../../../redux/slices/appState/appStateSlice";
import {
  OccupationalFunctionOtherKey,
  ParticipantTabFormKey,
} from "../../../../../../modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";

interface ParticipantCareerGoalFormProps {
  pageId: string;
  apiSkills: IdNameList[];
  apiTechnicalSkills: IdNameList[];
}

export const ParticipantCareerGoal: FC<ParticipantCareerGoalFormProps> = ({
  pageId,
  apiSkills,
  apiTechnicalSkills,
}) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();

  const { control, getValues, watch, setValue, clearErrors } = useFormContext();
  const dispatch = useDispatch();

  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.careerGoal, key);
  };

  const getDropdownValues = (key: string) => {
    return fetchDropdownValues(programSiteCoreKeys.careerGoal, key);
  };

  const skillsRef = useRef<{ resetValues: (values?: string[]) => void }>(null);
  const technicalSkillsRef = useRef<{
    resetValues: (values?: string[]) => void;
  }>(null);

  const isDisabled = React.useMemo(() => {
    return (
      getValues(ParticipantTabFormKey.targetOccupationFunctionId) !==
      OccupationalFunctionOtherKey
    );
  }, [watch(ParticipantTabFormKey.targetOccupationFunctionId)]);

  const {
    data: getTargetIndustries,
    isFetching: isGetTargetIndustriesPending,
    isError: isGetTargetIndustriesError,
  } = useGetTargetIndustries();
  const {
    data: getCurrencies,
    isFetching: isGetCurrenciesPending,
    isError: isGetCurrenciesError,
  } = useGetCurrencies();
  const {
    data: getSkills,
    isFetching: isGetSkillsPending,
    isError: isGetSkillsError,
  } = useGetSkills();
  const {
    data: getTechnicalSkills,
    isFetching: isGetTechnicalSkillsPending,
    isError: isGetTechnicalSkillsError,
  } = useGetTechnicalskills();

  useEffect(() => {
    if (isNotEmpty(getSkills) && isNotEmpty(apiSkills)) {
      skillsRef.current?.resetValues(
        apiSkills.map((item: IdNameList) => item.id),
      );
    }
  }, [getSkills, apiSkills]);

  useEffect(() => {
    if (isNotEmpty(getTechnicalSkills) && isNotEmpty(apiTechnicalSkills)) {
      technicalSkillsRef.current?.resetValues(
        apiTechnicalSkills.map((item: IdNameList) => item.id),
      );
    }
  }, [getTechnicalSkills, apiTechnicalSkills]);

  useEffect(() => {
    if (
      isGetCurrenciesError ||
      isGetTargetIndustriesError ||
      isGetSkillsError ||
      isGetTechnicalSkillsError
    ) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
    }

    if (
      isGetTargetIndustriesPending ||
      isGetCurrenciesPending ||
      isGetSkillsPending ||
      isGetTechnicalSkillsPending
    ) {
      dispatch(
        toggleLoader({
          isVisible:
            isGetTargetIndustriesPending ||
            isGetCurrenciesPending ||
            isGetSkillsPending ||
            isGetTechnicalSkillsPending,
          message: commonLabels.loadingPleaseWait,
        }),
      );
    }
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isGetTargetIndustriesPending,
    isGetTargetIndustriesError,
    isGetCurrenciesPending,
    isGetCurrenciesError,
    isGetSkillsPending,
    isGetSkillsError,
    isGetTechnicalSkillsPending,
    isGetTechnicalSkillsError,
  ]);

  const isStartJobSearch = useMemo(() => {
    return (
      getValues(ParticipantTabFormKey.participantReadyToStartJobSearch) ===
      programSiteCoreKeys.participantStartJobSearchYes
    );
  }, [watch(ParticipantTabFormKey.participantReadyToStartJobSearch)]);

  const handleStartJobSearchChange = (value: string) => {
    clearErrors(ParticipantTabFormKey.participantJobSearchStartDate);
    if (value !== programSiteCoreKeys.participantStartJobSearchYes) {
      setValue(ParticipantTabFormKey.participantJobSearchStartDate, "");
    } else {
      setValue(
        ParticipantTabFormKey.participantJobSearchStartDate,
        dayjs().format(userCountryDateFormat),
      );
    }
  };

  return (
    <>
      <MuiBox
        className="sponsoring-employer-details"
        data-testid="sponsoring-employer"
      >
        <MuiGrid container className="main-container">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.targetJobTitle}
            >
              <MuiTextField
                id={`${pageId}-${ParticipantTabFormKey.targetJobTitle}`}
                label={getLabel(programSiteCoreKeys.targetJobTitle)}
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_100}
                showCloseIcon
                requiredARIALabel={commonARIALabel.required}
                tooltipMessage={
                  commonValidationMessages.fieldSpecialCharactersTooltipMessage
                }
                tooltipHeaderTitle={
                  commonValidationMessages.allowedSpecialCharactersTitle
                }
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.targetOccupationFunctionId}
            >
              <MuiSelect
                label={getLabel(programSiteCoreKeys.targetOccupationalFunction)}
                options={getDropdownValues(
                  programSiteCoreKeys.occupationalFunctionListCareerGoal,
                )}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ParticipantTabFormKey.targetOccupationFunctionId}`}
                placeholder={commonLabels.selectPlaceHolder}
                onChange={(value) => {
                  setValue(
                    ParticipantTabFormKey.targetOccupationOtherFunction,
                    "",
                  );
                }}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.targetOccupationOtherFunction}
            >
              <MuiTextField
                id={`${pageId}-${ParticipantTabFormKey.targetOccupationOtherFunction}`}
                label={getLabel(
                  programSiteCoreKeys.targetOccupationalFunctionOther,
                )}
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_100}
                disabled={isDisabled}
                showCloseIcon
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.targetOccupationLevelId}
            >
              <MuiSelect
                label={getLabel(programSiteCoreKeys.targetOccupationalLevel)}
                options={getDropdownValues(
                  programSiteCoreKeys.occupationalLevelListCareerGoal,
                )}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ParticipantTabFormKey.targetOccupationLevelId}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.targetIndustryId}
            >
              <MuiSelect
                label={getLabel(programSiteCoreKeys.targetIndustryCareerGoal)}
                options={setApiDataToDropDownOption(getTargetIndustries ?? [])}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ParticipantTabFormKey.targetIndustryId}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.targetOrganisation}
            >
              <MuiTextField
                id={`${pageId}-${ParticipantTabFormKey.targetOrganisation}`}
                label={getLabel(programSiteCoreKeys.targetOrganization)}
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_100}
                showCloseIcon
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.targetJobTypeId}
            >
              <MuiSelect
                label={getLabel(programSiteCoreKeys.jobType)}
                options={getDropdownValues(programSiteCoreKeys.jobTypeList)}
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ParticipantTabFormKey.targetJobTypeId}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.targetLocation}
            >
              <MuiTextField
                id={`${pageId}-${ParticipantTabFormKey.targetLocation}`}
                label={getLabel(programSiteCoreKeys.targetLocation)}
                placeholder={commonLabels.inputPlaceHolder}
                maxLength={MAX_LENGTH_100}
                showCloseIcon
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiGrid container className="currency-container-main">
              <FormController
                control={control}
                name={ParticipantTabFormKey.currenciesId}
              >
                <MuiSelect
                  label={getLabel(programSiteCoreKeys.targetSalaryRange)}
                  options={setApiDataToDropDownOption(getCurrencies ?? [])}
                  className="currency-container"
                  visibleDefaultSelectOption
                  value=""
                  id={`${pageId}-${ParticipantTabFormKey.targetIndustryId}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
              <FormController
                control={control}
                name={ParticipantTabFormKey.salaryRangeId}
              >
                <MuiSelect
                  label={""}
                  options={getDropdownValues(
                    programSiteCoreKeys.salaryRangeList,
                  )}
                  visibleDefaultSelectOption
                  className="currency-range-container"
                  value=""
                  id={`${pageId}-${ParticipantTabFormKey.salaryRangeId}`}
                  placeholder={commonLabels.selectPlaceHolder}
                  requiredARIALabel={commonARIALabel.required}
                />
              </FormController>
            </MuiGrid>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.skills}
              ref={skillsRef}
            >
              <MuiMultiSelect
                id={`${pageId}-${ParticipantTabFormKey.skills}`}
                label={getLabel(programSiteCoreKeys.skillsKeywords)}
                options={setApiDataToDropDownOption(getSkills ?? [])}
                showChip={true}
                dialogTitle={getLabel(programSiteCoreKeys.skillsKeywords)}
                selectPlaceHolder={commonLabels.selectPlaceHolder}
                searchPlaceHolder={commonLabels.search}
                selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                doneText={commonLabels.done}
                allARIAProps={multiSelectARIALabels}
              />
            </FormController>
          </MuiGrid>
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.technicalSkills}
              ref={technicalSkillsRef}
            >
              <MuiMultiSelect
                id={`${pageId}-${ParticipantTabFormKey.technicalSkills}`}
                label={getLabel(programSiteCoreKeys.technicalSkills)}
                options={setApiDataToDropDownOption(getTechnicalSkills ?? [])}
                showChip={true}
                dialogTitle={getLabel(programSiteCoreKeys.technicalSkills)}
                selectPlaceHolder={commonLabels.selectPlaceHolder}
                searchPlaceHolder={commonLabels.search}
                selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                doneText={commonLabels.done}
                allARIAProps={multiSelectARIALabels}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>

        <MuiGrid container className="main-container">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <FormController
              control={control}
              name={ParticipantTabFormKey.participantReadyToStartJobSearch}
            >
              <MuiSelect
                label={getLabel(
                  programSiteCoreKeys.participantReadyToStartJobSearch,
                )}
                options={fetchDropdownValues(
                  programSiteCoreKeys.careerGoal,
                  programSiteCoreKeys.participantStartJobSearchList,
                )}
                onChange={(e) =>
                  handleStartJobSearchChange(e.target.value as string)
                }
                visibleDefaultSelectOption
                value=""
                id={`${pageId}-${ParticipantTabFormKey.participantReadyToStartJobSearch}`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
                isMandatory
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDateFieldFormController
              control={control}
              name={ParticipantTabFormKey.participantJobSearchStartDate}
              className="date-picker"
              label={getLabel(
                programSiteCoreKeys.participantJobSearchStartDate,
              )}
              id={`${pageId}-${ParticipantTabFormKey.participantJobSearchStartDate}`}
              disableManualInput
              format={userCountryDateFormat}
              valueFormat={userCountryDateFormat}
              value={
                getValues(
                  ParticipantTabFormKey.participantJobSearchStartDate,
                ) !== ""
                  ? dayjs(
                      getValues(
                        ParticipantTabFormKey.participantJobSearchStartDate,
                      ),
                      userCountryDateFormat,
                    )
                  : null
              }
              requiredARIALabel={commonARIALabel.required}
              selectDateARIALabel={commonARIALabel.selectDate}
              isMandatory={isStartJobSearch}
              disabled={!isStartJobSearch}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
      <MuiBox className="select-preference-container">
        <MuiBox className="select-preference-contain">
          <MuiTypography
            component={"h2"}
            className="career-goal-label-style"
            variant={"h2Bold"}
          >
            {getLabel(programSiteCoreKeys.selectPreferences)}
          </MuiTypography>
          <MuiGrid container className="checkbox-container-career-goal">
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormControllerCheckbox
                control={control}
                name={ParticipantTabFormKey.participantNotSeekingEmployment}
              >
                <MuiCheckbox
                  checkboxLabel={getLabel(
                    programSiteCoreKeys.participantNotSeekingEmployment,
                  )}
                  id={`${pageId}-${ParticipantTabFormKey.participantNotSeekingEmployment}`}
                  data-testid={`${pageId}-${ParticipantTabFormKey.participantNotSeekingEmployment}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormControllerCheckbox
                control={control}
                name={ParticipantTabFormKey.participantNotWillngToWorkRemotely}
              >
                <MuiCheckbox
                  checkboxLabel={getLabel(
                    programSiteCoreKeys.participantWillingToWorkRemotely,
                  )}
                  id={`${pageId}-${ParticipantTabFormKey.participantNotWillngToWorkRemotely}`}
                  data-testid={`${pageId}-${ParticipantTabFormKey.participantNotWillngToWorkRemotely}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormControllerCheckbox
                control={control}
                name={ParticipantTabFormKey.openToRelocation}
              >
                <MuiCheckbox
                  checkboxLabel={getLabel(programSiteCoreKeys.openToRelocation)}
                  id={`${pageId}-${ParticipantTabFormKey.openToRelocation}`}
                  data-testid={`${pageId}-${ParticipantTabFormKey.openToRelocation}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
            <MuiGrid size={gridSize} className={gridSizeMediumClass}>
              <FormControllerCheckbox
                control={control}
                name={ParticipantTabFormKey.participantWillingToTravelForWork}
              >
                <MuiCheckbox
                  checkboxLabel={getLabel(
                    programSiteCoreKeys.participantWillingToTravelForWork,
                  )}
                  id={`${pageId}-${ParticipantTabFormKey.participantWillingToTravelForWork}`}
                  data-testid={`${pageId}-${ParticipantTabFormKey.participantWillingToTravelForWork}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
      </MuiBox>
      <MuiBox className="select-preference-container">
        <MuiBox className="select-preference-authorized">
          <MuiTypography
            id="id-group-label"
            component={"h2"}
            className="career-goal-label-style"
            variant={"h2Bold"}
          >
            {getLabel(programSiteCoreKeys.selectAuthorisedConnections)}
          </MuiTypography>
          <MuiInfoToolTip
            isRemoveCloseIcon
            message={getLabel(programSiteCoreKeys.popUpInfo)}
          />
        </MuiBox>
        <MuiBox
          role="group"
          aria-labelledby="id-group-label"
          className="select-preference-contain"
        >
          <MuiGrid
            role="list"
            container
            className="checkbox-container-career-goal"
          >
            <MuiGrid
              size={gridSize}
              className={gridSizeMediumClass}
              role="listitem"
            >
              <FormControllerCheckbox
                control={control}
                name={ParticipantTabFormKey.experis}
              >
                <MuiCheckbox
                  checkboxLabel={getLabel(programSiteCoreKeys.experis)}
                  id={`${pageId}-${ParticipantTabFormKey.experis}`}
                  data-testid={`${pageId}-${ParticipantTabFormKey.experis}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
            <MuiGrid
              size={gridSize}
              className={gridSizeMediumClass}
              role="listitem"
            >
              <FormControllerCheckbox
                control={control}
                name={ParticipantTabFormKey.manpowerEngineering}
              >
                <MuiCheckbox
                  checkboxLabel={getLabel(
                    programSiteCoreKeys.manPowerEngineering,
                  )}
                  id={`${pageId}-${ParticipantTabFormKey.manpowerEngineering}`}
                  data-testid={`${pageId}-${ParticipantTabFormKey.manpowerEngineering}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
            <MuiGrid
              size={gridSize}
              className={gridSizeMediumClass}
              role="listitem"
            >
              <FormControllerCheckbox
                control={control}
                name={ParticipantTabFormKey.jeffersonWells}
              >
                <MuiCheckbox
                  checkboxLabel={getLabel(programSiteCoreKeys.jeffersonWells)}
                  id={`${pageId}-${ParticipantTabFormKey.jeffersonWells}`}
                  data-testid={`${pageId}-${ParticipantTabFormKey.jeffersonWells}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
            <MuiGrid
              size={gridSize}
              className={gridSizeMediumClass}
              role="listitem"
            >
              <FormControllerCheckbox
                control={control}
                name={ParticipantTabFormKey.rpo}
              >
                <MuiCheckbox
                  checkboxLabel={getLabel(programSiteCoreKeys.rpo)}
                  id={`${pageId}-${ParticipantTabFormKey.rpo}`}
                  data-testid={`${pageId}-${ParticipantTabFormKey.rpo}`}
                />
              </FormControllerCheckbox>
            </MuiGrid>
          </MuiGrid>
        </MuiBox>
      </MuiBox>
    </>
  );
};
